import { useCallback } from 'react';

import type { AgentMemoryRule } from '@/hooks/profile/useAgentMemory';
import { Badge } from '@/lib/base/badges/badges';
import { Toggle } from '@/lib/base/forms/toggle';
import { cx } from '@/utils/cx';

import { RuleEvidence } from './RuleEvidence';

interface MemoryRuleRowProps {
  rule: AgentMemoryRule;
  onVeto: (ruleId: string) => void | Promise<void>;
  isVetoing: boolean;
}

export function MemoryRuleRow({ rule, onVeto, isVetoing }: MemoryRuleRowProps) {
  const isVetoed = rule.status === 'vetoed';

  const handleChange = useCallback(
    (next: boolean) => {
      if (!next) {
        void onVeto(rule.id);
      }
    },
    [onVeto, rule.id]
  );

  return (
    <div className='py-2'>
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0 flex-1'>
          <p
            className={cx(
              'text-sm text-fg-primary',
              isVetoed && 'line-through text-fg-tertiary'
            )}
          >
            {rule.text}
          </p>
          {isVetoed && (
            <Badge color='gray' size='sm' className='mt-1'>
              Off
            </Badge>
          )}
          {rule.status === 'candidate' && (
            <Badge color='gray' size='sm' className='mt-1'>
              Learning
            </Badge>
          )}
        </div>
        <Toggle
          isSelected={!isVetoed}
          isDisabled={isVetoed || isVetoing}
          size='md'
          onChange={handleChange}
          aria-label={`Apply rule: ${rule.text}`}
        />
      </div>
      {rule.evidence.length > 0 && <RuleEvidence evidence={rule.evidence} />}
    </div>
  );
}
